import { useClubSlug } from "components/Clubs/hooks/useClubSlug";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useApi } from "../../../hooks/useApi";
import useAuth from "../../../hooks/useAuth";
import { useClubMemberAuthStore } from "../../ClubMemberManagement/ClubMember/ClubMemberAuthorization";

export type Field = {
    label: string;
    type: "text" | string;
    name: string;
    autofill: string | null;
    options?: unknown[];
};

const normalizeOption = (value: unknown) =>
    String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss');

// Member genders are stored as männlich/weiblich/divers, forms often ask for Herr/Frau or m/w/d instead.
const OPTION_ALIASES: Record<string, string[]> = {
    maennlich: ['m', 'male', 'mann', 'herr', 'junge', 'maennlich'],
    weiblich: ['w', 'f', 'female', 'frau', 'maedchen', 'weiblich'],
    divers: ['d', 'divers', 'diverse', 'other', 'anderes', 'keine angabe']
};

/** Picks the option of a select field that stands for the resolved value, or null when none does. */
const matchSelectOption = (options: unknown[], value: unknown): string | null => {
    const labels = options.filter((option): option is string | number => ['string', 'number'].includes(typeof option));
    const needle = normalizeOption(value);
    const exact = labels.find((option) => normalizeOption(option) === needle);

    if (exact !== undefined) return String(exact);

    const aliases = OPTION_ALIASES[needle] ?? [];
    const aliased = labels.find((option) => aliases.includes(normalizeOption(option)));

    return aliased !== undefined ? String(aliased) : null;
};

export interface AdditionalData {
    form_data_id: number;
    bookable_title: string;
    created_at: string;
    name: string;
    data: Record<string, any>;
}

export interface AutoFillReturn {
    isLoading: boolean;
    isLoadingAdditionalData: boolean;
    data: any;
    additionalData: AdditionalData[];
}

export interface AutoFillProps {
    fields: Field[];
    namePrefix?: string;
    clubSlug?: string;
    formId?: number;
    setValues?: boolean;
    email?: string;
    /** The club member the booking is for — decides whose data is filled in. */
    memberId?: number | string | null;
}

export const useAutoFill = ({
    fields,
    namePrefix = "formData",
    clubSlug: _clubSlug,
    formId,
    email,
    memberId,
    setValues = true,
}: AutoFillProps): AutoFillReturn => {
    const clubSlug = useClubSlug(_clubSlug);
    const { isAuthenticated, isLoading: isLoadingAuth } = useAuth();
    const { setValue } = useFormContext();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
    const [data, setData] = useState({});
    const [additionalData, setAdditionalData] = useState<AdditionalData[]>([]);
    const api = useApi();
    const { clubMemberAuthToken } = useClubMemberAuthStore();

    /** Apply resolved autofill values to the form fields under the current namePrefix. */
    const setAutoFillValues = useCallback(
        (resolvedData: Record<string, string>) => {
            Object.entries(resolvedData).forEach(([key, value]) => {
                const field = (fields || []).find((candidate) => candidate.name === key);

                if (field?.type === 'select' && Array.isArray(field.options)) {
                    const option = matchSelectOption(field.options, value);
                    if (option !== null) setValue(`${namePrefix}.${key}`, option);

                    return;
                }

                setValue(`${namePrefix}.${key}`, value);
            });
        },
        [fields, namePrefix, setValue]
    );

    /** Fetch member/user data from the backend and pre-populate form fields. */
    const fetchAutoFillData = useCallback(() => {
        if (clubSlug && isAuthenticated && !isLoadingAuth) {
            const mappedFields = (fields || [])
                .filter((field) => field.autofill)
                .reduce((carry, field) => ({ ...carry, [field.name]: field.autofill }), {});

            if (Object.keys(mappedFields).length > 0) {
                setIsLoading(true);
                void api({
                    url: `form/${clubSlug}/autofill`,
                    method: "POST",
                    data: {
                        fields: mappedFields,
                        clubMemberAuthToken,
                        email,
                        club_member_id: memberId,
                    },
                    muted: true,
                    onSuccess: (responseData) => {
                        setData(responseData.data);
                        if (setValues) setAutoFillValues(responseData.data);
                    },
                    onFinish: () => setIsLoading(false),
                });
            }
        }
    }, [
        fields,
        clubSlug,
        email,
        memberId,
        clubMemberAuthToken,
        isLoadingAuth,
        isAuthenticated,
        setAutoFillValues,
        setValues,
    ]);

    /** Fetch previous form submissions so the user can re-apply earlier answers. */
    const fetchFillableData = useCallback(() => {
        if (formId && clubSlug && isAuthenticated && !isLoadingAuth) {
            const hasFillableFields = (fields || []).some((field) => field.autofill);

            if (hasFillableFields) {
                setIsLoadingAdditionalData(true);
                void api({
                    url: `form/${clubSlug}/autofill/${formId}`,
                    method: "GET",
                    muted: true,
                    onSuccess: (responseData) => {
                        setAdditionalData(responseData.additional_data ?? []);
                    },
                    onFinish: () => setIsLoadingAdditionalData(false),
                });
            }
        }
    }, [fields, clubSlug, formId, isLoadingAuth, isAuthenticated]);

    useEffect(() => fetchAutoFillData(), [fetchAutoFillData]);
    useEffect(() => fetchFillableData(), [fetchFillableData]);

    return { isLoading, isLoadingAdditionalData, data, additionalData };
};
